import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import serviceApi from '../services/serviceApi';
import { SUBSCRIPTION_STATUS_LABELS } from '../config/serviceConstants';
import PricePreview from './services/PricePreview';

/**
 * EditSubscriptionModal — Stage 6 immutability UX (decision #30).
 *
 * `packageCode`/`commercials`/`packageSnapshot`/`billingCycleStart`/`validityDays`
 * are immutable once any visit has been consumed. UI gates the relevant
 * inputs and surfaces the rule clearly. Backend enforces the rule too —
 * UI is a usability layer, not the security layer.
 */
const EditSubscriptionModal = ({ show, onHide, subscription, onUpdated }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    visitsIncluded: '',
    priceBeforeVAT: '',
    priceNotes: '',
    status: ''
  });

  useEffect(() => {
    if (subscription && show) {
      setForm({
        visitsIncluded: subscription.visitsIncluded?.toString() || '',
        priceBeforeVAT: subscription.priceBeforeVAT?.toString() || '',
        priceNotes: subscription.priceNotes || '',
        status: subscription.status || 'active'
      });
      setError(null);
    }
  }, [subscription, show]);

  if (!subscription) return null;

  // Decision #30 — once any visit consumed, package details locked.
  // visitsConsumed approx = visitsIncluded − effectiveBalance.
  const balance = typeof subscription.effectiveBalance === 'number'
    ? subscription.effectiveBalance
    : null;
  const visitsConsumed = balance !== null && subscription.visitsIncluded
    ? Math.max(0, subscription.visitsIncluded - balance)
    : (subscription.visitsUsed || 0);
  const packageLocked = visitsConsumed > 0;

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    setError(null);
    setSaving(true);

    try {
      const updates = {};
      if (!packageLocked && form.visitsIncluded !== subscription.visitsIncluded?.toString()) {
        updates.visitsIncluded = parseInt(form.visitsIncluded, 10);
      }
      if (!packageLocked && form.priceBeforeVAT !== subscription.priceBeforeVAT?.toString()) {
        updates.priceBeforeVAT = parseFloat(form.priceBeforeVAT);
      }
      if (form.priceNotes !== (subscription.priceNotes || '')) {
        updates.priceNotes = form.priceNotes;
      }
      if (form.status !== subscription.status) {
        updates.status = form.status;
      }

      if (Object.keys(updates).length === 0) {
        onHide();
        return;
      }

      await serviceApi.updateSubscription(subscription._id, updates);
      onUpdated();
      onHide();
    } catch (err) {
      setError(err.response?.data?.error || 'حدث خطأ — يرجى المحاولة مرة أخرى');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered dir="rtl">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 700, color: '#471396' }}>
          تعديل الاشتراك
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}

        <div style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: 12 }}>
          الموقع: <strong>{subscription.locationId?.name || '—'}</strong>
        </div>

        {subscription.packageSnapshot && subscription.packageSnapshot.packageCode && (
          <div style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: 8 }}>
            الباقة: <strong>{subscription.packageSnapshot.packageName}</strong>
          </div>
        )}

        {packageLocked && (
          <Alert variant="warning" className="py-2" style={{ fontSize: '0.85rem' }}>
            ⚠️ تم استهلاك {visitsConsumed} زيارة — لا يمكن تعديل تفاصيل الباقة.
            للتغيير: استخدم تجديد الاشتراك أو أنشئ اشتراكاً جديداً.
          </Alert>
        )}

        {subscription.commercials && (
          <PricePreview commercials={subscription.commercials} label="السعر الحالي (مجمد)" />
        )}

        <div className="d-flex gap-3 mb-3">
          <Form.Group style={{ flex: 1 }}>
            <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>
              عدد الزيارات {packageLocked && <span style={{ color: '#856404' }}>🔒</span>}
            </Form.Label>
            <Form.Control
              className="touch-input"
              type="number"
              min="1"
              value={form.visitsIncluded}
              onChange={(e) => handleChange('visitsIncluded', e.target.value)}
              disabled={packageLocked}
            />
          </Form.Group>
          <Form.Group style={{ flex: 1 }}>
            <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>
              السعر قبل الضريبة (ر.س) {packageLocked && <span style={{ color: '#856404' }}>🔒</span>}
            </Form.Label>
            <Form.Control
              className="touch-input"
              type="number"
              min="0"
              step="0.01"
              value={form.priceBeforeVAT}
              onChange={(e) => handleChange('priceBeforeVAT', e.target.value)}
              disabled={packageLocked}
              dir="ltr"
            />
          </Form.Group>
        </div>

        <Form.Group className="mb-3">
          <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>حالة الاشتراك</Form.Label>
          <Form.Select
            className="touch-input"
            value={form.status}
            onChange={(e) => handleChange('status', e.target.value)}
          >
            {Object.entries(SUBSCRIPTION_STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label style={{ fontWeight: 600, fontSize: '0.88rem' }}>ملاحظات السعر</Form.Label>
          <Form.Control
            className="touch-input"
            as="textarea"
            rows={2}
            value={form.priceNotes}
            onChange={(e) => handleChange('priceNotes', e.target.value)}
            placeholder="اختياري..."
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>إلغاء</Button>
        <Button
          style={{ background: '#471396', borderColor: '#471396' }}
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? <span className="spinner-border spinner-border-sm" /> : 'حفظ التعديلات'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditSubscriptionModal;
